// ============================================
// PDF UPLOAD COMPONENT
// Upload and parse clinical documents
// ============================================

import React, { useState, useRef, useEffect } from 'react';
import { 
  Upload, FileText, Loader, AlertCircle, CheckCircle, 
  X, User, Calendar, Phone, Mail, MapPin, Stethoscope,
  AlertTriangle, ChevronDown, ChevronUp
} from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import app, { db, auth, storage } from './firebase';
import {
  collection, addDoc, deleteDoc, doc, query, where, orderBy, getDocs, serverTimestamp
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

const functions = getFunctions(app);
const parsePDF = httpsCallable(functions, 'parsePDF');

// An imported document is kept two ways, per Dr. Oyelese, 2026-09-26: the
// extracted text in Firestore so it can be read and searched, and the original
// file in Storage so the source is not lost. If Storage is unavailable the
// extraction is still saved, and the record says the original was not stored.
const saveDocument = async ({ patientId, file, extracted }) => {
  let storagePath = null;
  let storageError = null;

  try {
    const safe = file.name.replace(/[^A-Za-z0-9._-]/g, '_');
    storagePath = `patients/${patientId}/documents/${Date.now()}-${safe}`;
    await uploadBytes(ref(storage, storagePath), file, { contentType: 'application/pdf' });
  } catch (err) {
    console.error('Could not store the original file:', err);
    storagePath = null;
    storageError = err.code || err.message || 'upload failed';
  }

  const docRef = await addDoc(collection(db, 'documents'), {
    patientId,
    clinicianId: auth.currentUser?.uid || null,
    filename: file.name,
    contentType: 'application/pdf',
    size: file.size,
    extracted: extracted || null,
    storagePath,
    storageError,
    uploadedAt: serverTimestamp()
  });

  return { id: docRef.id, storagePath, storageError };
};

export const listDocuments = async (patientId) => {
  const q = query(collection(db, 'documents'),
                  where('patientId', '==', patientId),
                  orderBy('uploadedAt', 'desc'));
  const snap = await getDocs(q);
  const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));

  // Resolve the file URLs here, so the list can render a real link. Opening a
  // window after an await loses the user gesture and browsers block it — which
  // is why the Open button appeared to do nothing.
  return Promise.all(docs.map(async d => {
    if (!d.storagePath) return d;
    try { return { ...d, url: await getDownloadURL(ref(storage, d.storagePath)) }; }
    catch (err) { console.error('Could not resolve', d.filename, err); return d; }
  }));
};

// A document imported into the wrong chart has to come out — the file goes
// with the record, so nothing is orphaned in the bucket.
export const removeDocument = async (document) => {
  if (document.storagePath) {
    try { await deleteObject(ref(storage, document.storagePath)); }
    catch (err) { console.error('Could not delete the stored file:', err); }
  }
  await deleteDoc(doc(db, 'documents', document.id));
};

export const documentFileUrl = async (document) => {
  if (!document.storagePath) return null;
  try { return await getDownloadURL(ref(storage, document.storagePath)); }
  catch (err) { console.error('Could not resolve the file URL:', err); return null; }
};

// For Add Patient flow - extracts and returns data for form
export function PDFUploadForForm({ onExtracted, onError }) {
  const [loading, setLoading] = useState(false);
  const [filename, setFilename] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      onError?.('Please select a PDF file');
      return;
    }

    if (file.size > 7 * 1024 * 1024) {
      onError?.('File too large (max 7MB — base64 encoding adds ~33% and the upload limit is 10MB)');
      return;
    }

    setFilename(file.name);
    setLoading(true);

    try {
      const base64 = await fileToBase64(file);
      const result = await parsePDF({ pdfBase64: base64, filename: file.name });
      onExtracted?.(result.data);
    } catch (err) {
      console.error('PDF parsing error:', err);
      onError?.(err.message || 'Failed to parse PDF');
    } finally {
      setLoading(false);
    }
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  return (
    <div className="pdf-upload-inline">
      <input
        type="file"
        accept=".pdf"
        ref={fileInputRef}
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />
      <button
        type="button"
        className="pdf-upload-btn"
        onClick={() => fileInputRef.current?.click()}
        disabled={loading}
      >
        {loading ? (
          <>
            <Loader size={16} className="spinning" />
            <span>Parsing {filename}...</span>
          </>
        ) : (
          <>
            <Upload size={16} />
            <span>Upload Referral PDF</span>
          </>
        )}
      </button>
      {filename && !loading && (
        <span className="pdf-filename">
          <FileText size={14} />
          {filename}
        </span>
      )}
    </div>
  );
}

// For Patient Detail - full document upload with display
export function PDFUploadPanel({ patientId, onDocumentAdded }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [extracted, setExtracted] = useState(null);
  const [filename, setFilename] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const [saveNote, setSaveNote] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [openDoc, setOpenDoc] = useState(null);
  const fileInputRef = useRef(null);

  const refreshList = async () => {
    try { setDocuments(await listDocuments(patientId)); }
    catch (err) { console.error('Could not list documents:', err); }
  };

  // What is already in the chart, on open.
  useEffect(() => { if (patientId) refreshList(); /* eslint-disable-next-line */ }, [patientId]);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      setError('Please select a PDF file');
      return;
    }

    if (file.size > 7 * 1024 * 1024) {
      setError('File too large (max 7MB — base64 encoding adds ~33% and the upload limit is 10MB)');
      return;
    }

    setFilename(file.name);
    setLoading(true);
    setError(null);
    setExtracted(null);

    try {
      const base64 = await fileToBase64(file);
      const result = await parsePDF({ pdfBase64: base64, filename: file.name });
      setExtracted(result.data);

      // Until now this panel parsed a document, showed it, and discarded it:
      // `patientId` was never used and `onDocumentAdded` was never passed, so
      // nothing reached the chart. It is saved here.
      try {
        const saved = await saveDocument({ patientId, file, extracted: result.data });
        setSaveNote(saved.storageError
          ? `Saved to the chart. The original file could not be stored (${saved.storageError}) — the extracted text is kept.`
          : 'Saved to the chart, with the original file.');
        await refreshList();
        onDocumentAdded?.({
          id: saved.id,
          filename: file.name,
          uploadedAt: new Date(),
          extracted: result.data
        });
      } catch (saveErr) {
        console.error('Could not save the document:', saveErr);
        setSaveNote('Parsed, but NOT saved to the chart — ' + (saveErr.message || 'unknown error'));
      }
    } catch (err) {
      console.error('PDF parsing error:', err);
      setError(err.message || 'Failed to parse PDF');
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (document) => {
    if (!window.confirm(`Remove "${document.filename}" from this chart? This cannot be undone.`)) return;
    try { await removeDocument(document); await refreshList(); }
    catch (err) { console.error('Could not remove the document:', err); setError('Could not remove that document.'); }
  };


  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const clearExtracted = () => {
    setExtracted(null);
    setFilename(null);
    setError(null);
    setSaveNote(null);
  };

  return (
    <div className="pdf-upload-panel">
      <input
        type="file"
        accept=".pdf"
        ref={fileInputRef}
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      {/* Upload Area */}
      {!extracted && !loading && (
        <div 
          className="pdf-dropzone"
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload size={32} />
          <p>Click to upload a clinical document</p>
          <span>PDF files up to 7MB</span>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="pdf-loading">
          <Loader size={32} className="spinning" />
          <p>Parsing {filename}...</p>
          <span>AI is extracting clinical information</span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="pdf-error">
          <AlertCircle size={24} />
          <p>{error}</p>
          <button onClick={clearExtracted}>Try Again</button>
        </div>
      )}

      {/* Whether it reached the chart — stated plainly, because for a long
          time this panel parsed documents and saved none of them. */}
      {saveNote && (
        <div style={{
          margin: '10px 0', padding: '8px 12px', borderRadius: '8px', fontSize: '13px',
          background: saveNote.startsWith('Saved') ? 'rgba(34,197,94,0.12)' : 'rgba(251,191,36,0.12)',
          border: `1px solid ${saveNote.startsWith('Saved') ? 'rgba(34,197,94,0.4)' : 'rgba(251,191,36,0.4)'}`,
          color: saveNote.startsWith('Saved') ? '#22c55e' : '#fbbf24'
        }}>{saveNote}</div>
      )}

      {/* What is already in this chart */}
      {documents.length > 0 && (
        <div style={{ marginTop: '14px' }}>
          <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '6px',
                        textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            In this chart · {documents.length}
          </div>
          {documents.map(d => (
            <React.Fragment key={d.id}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '10px',
              padding: '8px 10px', marginBottom: '6px', borderRadius: '8px',
              background: 'rgba(15,23,42,0.4)', border: '1px solid rgba(100,116,139,0.25)'
            }}>
              <FileText size={16} style={{ color: '#64b5f6', flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: '#e2e8f0', fontSize: '13px', overflow: 'hidden',
                              textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.filename}</div>
                <div style={{ color: '#64748b', fontSize: '11px' }}>
                  {d.uploadedAt?.toDate?.().toLocaleString() || 'just now'}
                  {d.extracted?.document_type ? ` · ${d.extracted.document_type}` : ''}
                  {d.storagePath ? '' : ' · original not stored'}
                </div>
              </div>
              {d.extracted && (
                <button onClick={() => setOpenDoc(openDoc === d.id ? null : d.id)}
                  title="What Rebuild read from this document"
                  style={{ background: 'none', border: '1px solid rgba(100,116,139,0.5)',
                           color: '#94a3b8', borderRadius: '6px', padding: '4px 10px',
                           fontSize: '12px', cursor: 'pointer' }}>
                  {openDoc === d.id ? 'Hide' : 'Read'}
                </button>
              )}
              {d.url && (
                <a href={d.url} target="_blank" rel="noopener noreferrer" title="Open the original PDF"
                  style={{ border: '1px solid rgba(100,181,246,0.4)', textDecoration: 'none',
                           color: '#64b5f6', borderRadius: '6px', padding: '4px 10px',
                           fontSize: '12px' }}>Open</a>
              )}
              <button onClick={() => handleRemove(d)} title="Remove from this chart"
                style={{ background: 'none', border: 'none', color: '#64748b',
                         cursor: 'pointer', padding: '4px' }}>
                <X size={16} />
              </button>
              </div>
              {openDoc === d.id && d.extracted && (
                <div style={{ margin: '-2px 0 8px 0', padding: '12px 14px', borderRadius: '10px',
                              background: '#ffffff', color: '#111827',
                              border: '1px solid #e2e8f0' }}>
                  {d.extracted.flags?.length > 0 && (
                    <div style={{ color: '#854d0e', fontSize: '12px', fontWeight: 600, marginBottom: '6px' }}>
                      ⚠ {d.extracted.flags.join(' · ')}
                    </div>
                  )}
                  {d.extracted.summary && (
                    <div style={{ color: '#1f2937', fontSize: '13px', lineHeight: 1.6,
                                  marginBottom: '6px' }}>{d.extracted.summary}</div>
                  )}
                  {d.extracted.clinical_info?.presenting_concerns?.length > 0 && (
                    <div style={{ color: '#6b7280', fontSize: '12px' }}>
                      Concerns · {d.extracted.clinical_info.presenting_concerns.join('; ')}
                    </div>
                  )}
                  {d.extracted.clinical_info?.current_medications?.length > 0 && (
                    <div style={{ color: '#6b7280', fontSize: '12px' }}>
                      Medications · {d.extracted.clinical_info.current_medications.join('; ')}
                    </div>
                  )}
                  <div style={{ color: '#9ca3af', fontSize: '11px', marginTop: '6px' }}>
                    Read by the AI extractor from the document. It is a reading, not the
                    document — open the original above for what was actually written.
                  </div>
                </div>
              )}
              </React.Fragment>
          ))}
        </div>
      )}

      {/* Extracted Results */}
      {extracted && (
        <div className="pdf-extracted">
          <div className="pdf-extracted-header">
            <div className="pdf-extracted-title">
              <FileText size={20} />
              <div>
                <h4>{filename}</h4>
                <span>{extracted.document_type}</span>
              </div>
            </div>
            <div className="pdf-extracted-actions">
              <button onClick={() => setExpanded(!expanded)}>
                {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
              </button>
              <button onClick={clearExtracted}>
                <X size={18} />
              </button>
            </div>
          </div>

          {expanded && (
            <>
              {/* Flags */}
              {extracted.flags && extracted.flags.length > 0 && (
                <div className="pdf-flags">
                  {extracted.flags.map((flag, i) => (
                    <div key={i} className="pdf-flag">
                      <AlertTriangle size={14} />
                      <span>{flag}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Summary */}
              <div className="pdf-summary">
                <p>{extracted.summary}</p>
              </div>

              {/* Patient Info */}
              {extracted.patient_info && hasAnyValue(extracted.patient_info) && (
                <div className="pdf-section">
                  <h5>Patient Information</h5>
                  <div className="pdf-info-grid">
                    {extracted.patient_info.name && (
                      <div className="pdf-info-item">
                        <User size={14} />
                        <span>{extracted.patient_info.name}</span>
                      </div>
                    )}
                    {extracted.patient_info.dob && (
                      <div className="pdf-info-item">
                        <Calendar size={14} />
                        <span>DOB: {extracted.patient_info.dob}</span>
                      </div>
                    )}
                    {extracted.patient_info.sex && (
                      <div className="pdf-info-item">
                        <User size={14} />
                        <span>{extracted.patient_info.sex}</span>
                      </div>
                    )}
                    {extracted.patient_info.healthcare_number && (
                      <div className="pdf-info-item">
                        <FileText size={14} />
                        <span>PHN: {extracted.patient_info.healthcare_number}</span>
                      </div>
                    )}
                    {extracted.patient_info.phone && (
                      <div className="pdf-info-item">
                        <Phone size={14} />
                        <span>{extracted.patient_info.phone}</span>
                      </div>
                    )}
                    {extracted.patient_info.email && (
                      <div className="pdf-info-item">
                        <Mail size={14} />
                        <span>{extracted.patient_info.email}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Clinical Info */}
              {extracted.clinical_info && hasAnyValue(extracted.clinical_info) && (
                <div className="pdf-section">
                  <h5>Clinical Information</h5>
                  
                  {extracted.clinical_info.referral_reason && (
                    <div className="pdf-clinical-item">
                      <label>Referral Reason</label>
                      <p>{extracted.clinical_info.referral_reason}</p>
                    </div>
                  )}

                  {extracted.clinical_info.presenting_concerns?.length > 0 && (
                    <div className="pdf-clinical-item">
                      <label>Presenting Concerns</label>
                      <ul>
                        {extracted.clinical_info.presenting_concerns.map((c, i) => (
                          <li key={i}>{c}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {extracted.clinical_info.relevant_history && (
                    <div className="pdf-clinical-item">
                      <label>Relevant History</label>
                      <p>{extracted.clinical_info.relevant_history}</p>
                    </div>
                  )}

                  {extracted.clinical_info.current_medications?.length > 0 && (
                    <div className="pdf-clinical-item">
                      <label>Current Medications</label>
                      <ul>
                        {extracted.clinical_info.current_medications.map((m, i) => (
                          <li key={i}>{m}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {extracted.clinical_info.referring_provider && (
                    <div className="pdf-clinical-item">
                      <label>Referring Provider</label>
                      <p>{extracted.clinical_info.referring_provider}</p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* Upload Another */}
          <button 
            className="pdf-upload-another"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload size={16} />
            Upload Another Document
          </button>
        </div>
      )}
    </div>
  );
}

// Helper to check if object has any non-null values
function hasAnyValue(obj) {
  if (!obj) return false;
  return Object.values(obj).some(v => v !== null && v !== undefined && v !== '');
}

export default PDFUploadPanel;
